import { useSearchParams } from 'react-router-dom';
import {
  DEFAULT_REVIEW_TAB,
  isReviewTabValue,
  type ReviewTabValue,
} from '../constants/reviewTabs.ts';
import { DocumentTypeSchema } from '../model.ts';
import type { DocumentType } from '../model.ts';

const PARAM = {
  TAB: 'tab',
  KEYWORD: 'q',
  DOCUMENT_TYPE: 'documentType',
} as const;

const documentTypeParamSchema = DocumentTypeSchema.nullable().catch(null);

const useDocumentReviewFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get(PARAM.TAB);
  const tab: ReviewTabValue = isReviewTabValue(rawTab)
    ? rawTab
    : DEFAULT_REVIEW_TAB;
  const keyword = searchParams.get(PARAM.KEYWORD) ?? '';
  const documentType = documentTypeParamSchema.parse(
    searchParams.get(PARAM.DOCUMENT_TYPE),
  );

  const setParam = (key: string, value: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  };

  return {
    tab,
    keyword,
    documentType,
    setTab: (value: ReviewTabValue) => setParam(PARAM.TAB, value),
    setKeyword: (value: string) => setParam(PARAM.KEYWORD, value),
    setDocumentType: (value: DocumentType | null) =>
      setParam(PARAM.DOCUMENT_TYPE, value),
    resetFilters: () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams();
          const currentTab = prev.get(PARAM.TAB);
          if (currentTab) next.set(PARAM.TAB, currentTab);
          return next;
        },
        { replace: true },
      );
    },
  };
};

export { useDocumentReviewFilters };
